import React, { useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import ServiceInvoiceScreen from "./serviceInvoice/ServiceInvoiceScreen";
import ExciseInvoiceScreen from "./exciseInvoice/ExciseInvoiceScreen";
import EditInvoiceModal from "./invoiceDetails/InvoiceEditDetails";
import { useDispatch, useSelector } from "react-redux";
import {
  setFormData,
  resetFormData,
} from "../../redux/features/slices/formDataSlice";
import * as styles from "./InvoiceScreenStyles";
import Toast from "../../common/Toast";
import { RootState } from "../../redux/store";
import downloadIcon from "../../assets/download-icon.png";
import {
  getInvoiceDetails,
  InvoiceDataType,
  updateInvoice,
} from "../../api/invoice";
import { downloadInvoiceAPI } from "../../api/orderFinalAPI";
import { useLocation, useNavigate } from "react-router-dom";

const InvoiceScreen = () => {
  const routerLocation = useLocation();
  const orderId = location.pathname.split("/").pop();
  const dispatch = useDispatch();
  const formData = useSelector((state: RootState) => state.formData);
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const orgId = localStorage.getItem("orgId");
  const navigate = useNavigate();
  const autoOpenedRef = React.useRef(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("edit");

  const handleOpenModal = () => {
    setModalMode("edit");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleConfirm = async (data: {
    exciseID: string;
    customerExecLeafNo: string;
    transporter: string;
    transporterPermitNo: string;
    vehicleNumber: string;
    serviceID?: string | null;
  }) => {
    // Update Redux
    dispatch(setFormData(data));

    // Update local state for UI consistency
    setValues((prev) => ({
      ...prev!,
      excise: {
        ...(prev?.excise || {}),
        exciseID: data.exciseID,
      },
      other: {
        ...(prev?.other || {}),
        customerExecLeafNo: data.customerExecLeafNo,
        transporter: data.transporter,
        transporterPermitNo: data.transporterPermitNo,
        vehicleNumber: data.vehicleNumber,
      },
    }));

    setOpenModal(false);

    // Persist all details via update API
    if (
      data.exciseID &&
      data.customerExecLeafNo &&
      data.transporter &&
      data.transporterPermitNo &&
      data.vehicleNumber
    ) {
      try {
        const response = await updateInvoice(
          orderId!,
          data.customerExecLeafNo ?? "",
          data.transporterPermitNo ?? "",
          data.transporter ?? "",
          data.vehicleNumber ?? "",
          data.exciseID ?? "",
          data.serviceID
        );
        if (response.status === 201) {
          setToastMessage("Invoice details have been successfully updated.");
          setError(false);
        }
      } catch (err: any) {
        console.error(err);
        setToastMessage(err.message ?? "Failed to update the invoice.");
        setError(true);
      }
    } else {
      setToastMessage(
        "Failed to update the invoice. Please ensure all fields are correct and try again."
      );
      setError(true);
    }
    setShowToast(true);
  };
  const downloadInvoice = async () => {
    try {
      // Ensure the invoice is persisted on the backend before downloading.
      // If the user cancelled the "Enter Additional Invoice Details" modal,
      // updateInvoice was never called and the PDF wouldn't exist yet.
      // Send whatever values are currently available (empty strings allowed
      // for missing transporter / permit / vehicle fields).
      try {
        await updateInvoice(
          orderId!,
          values?.other?.customerExecLeafNo ??
            formData.customerExecLeafNo ??
            "",
          values?.other?.transporterPermitNo ??
            formData.transporterPermitNo ??
            "",
          values?.other?.transporter ?? formData.transporter ?? "",
          values?.other?.vehicleNumber ?? formData.vehicleNumber ?? "",
          values?.excise?.exciseID ?? formData.exciseID ?? "",
          includeServiceInvoice
            ? (values?.service?.serviceID ?? formData.serviceID ?? null)
            : null
        );
      } catch (persistErr) {
        console.error("Failed to persist invoice before download:", persistErr);
      }

      const response = await downloadInvoiceAPI(orderId!);
      if (response.data.data) {
        const link = document.createElement("a");
        link.href = response.data.data;
        link.download = `invoice-${orderId}.pdf`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setError(true);
      }
    } catch (error) {
      console.error(error);
      setError(true);
    }
  };
  useEffect(() => {
    const getValues = async () => {
      try {
        dispatch(resetFormData());
        setValues(undefined);

        const response = await getInvoiceDetails(orderId!);

        if (response?.data.data) {
          setValues(response.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch ", err);
      }
    };

    if (orderId) getValues();
  }, [orderId]);

  // Auto-open edit modal when arriving from Create Invoice action
  useEffect(() => {
    if (
      !autoOpenedRef.current &&
      routerLocation.state &&
      (routerLocation.state as { openEdit?: boolean }).openEdit
    ) {
      autoOpenedRef.current = true;
      setModalMode("create");
      setOpenModal(true);
    }
  }, [routerLocation.state]);

  const [values, setValues] = useState<InvoiceDataType>();
  const includeServiceInvoice = values?.includeService ?? true;

  return (
    <>
      <Box>
        <Box sx={styles.downloadStyle}>
          <Box
            component="img"
            sx={styles.downloadIconStyle}
            onClick={downloadInvoice}
            src={downloadIcon}
            alt="download-icon"
          />
          <Button
            variant="outlined"
            sx={styles.editButton}
            onClick={handleOpenModal}
          >
            Edit
          </Button>
        </Box>
      </Box>

      {/* Main Layout */}
      <Box sx={styles.invoiceLayoutBox}>
        {/* Excise Invoice */}
        <Box sx={styles.invoiceBox}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={styles.titleTypography}>
              Excise Invoice
            </Typography>
          </Box>
          <Box sx={styles.borderBox}>
            <ExciseInvoiceScreen data={values} />
          </Box>
        </Box>

        {/* Service Invoice */}
        {includeServiceInvoice && (
          <Box sx={styles.invoiceBox}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6" sx={styles.titleTypography}>
                Service Invoice
              </Typography>
            </Box>
            <Box sx={styles.borderBox}>
              <ServiceInvoiceScreen data={values} />
            </Box>
          </Box>
        )}
      </Box>

      {/* Modal Integration */}
      <EditInvoiceModal
        open={openModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
        title={
          modalMode === "create"
            ? "Enter Additional Invoice Details"
            : "Edit Invoice"
        }
        initialFormData={{
          exciseID: values?.excise?.exciseID ?? formData.exciseID ?? "",
          serviceID: includeServiceInvoice
            ? (values?.service?.serviceID ?? formData.serviceID)
            : null,
          customerExecLeafNo:
            values?.other?.customerExecLeafNo ??
            formData.customerExecLeafNo ??
            "",
          transporter: values?.other?.transporter ?? formData.transporter ?? "",
          transporterPermitNo:
            values?.other?.transporterPermitNo ??
            formData.transporterPermitNo ??
            "",
          vehicleNumber:
            values?.other?.vehicleNumber ?? formData.vehicleNumber ?? "",
        }}
        showServiceBillField={includeServiceInvoice}
      />

      {/* Toast Component */}
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />
    </>
  );
};

export default InvoiceScreen;
